// components/IndicatorsPage.jsx
// ─── KFX TradingView Suite — Premium Redesign ─────────────────
import { useState, useEffect } from 'react';
import { tradesApi } from '../utils/api.js';
import { calcConsecWinStreak } from '../utils/helpers.js';

import kfxPanelImg     from '../images/kfxpanel.png';
import kfxProImg       from '../images/kfxpro.png';
import kfxChecklistImg from '../images/kfxchecklist.png';
import kfxTradePanelImg from '../images/kfxtradepanel.png';
import kfxFibEaImg     from '../images/kfxfibea.png';
import '../styles/indicatorspage.css';

// ─────────────────────────────────────────────────────────────────
//  WHATSAPP "I'M INTERESTED" CTA
// ─────────────────────────────────────────────────────────────────
// Single source of truth for the sales number — change once, updates everywhere.
const WHATSAPP_NUMBER = '250792447050'; // 0792447050 written in international format (Rwanda +250)

function buildWhatsappLink(toolName) {
  const msg =
    `Hi KFX 👋 I saw the *${toolName}* is launching soon. ` +
    `I'd like to claim the founding price discount — can you send me the details?`;
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
}

// ─────────────────────────────────────────────────────────────────
//  INDICATOR DATA
// ─────────────────────────────────────────────────────────────────
const INDICATORS = [
  {
    id: 'kfxpanel',
    name: 'KFX Panel',
    category: 'Trade Management',
    tagline: 'Everything on your chart. Nothing off it.',
    description: 'All-in-one overlay for lot size, risk/reward, SL & TP, live trade status, trailing stop-loss and real-time balance.',
    color: '#E05555', rgb: '224,85,85',
    tvLink: null,
    comingSoon: true,
    thumbnail: kfxPanelImg,
    features: ['Lot size calculator', 'Live risk/reward', 'Trade status overlay', 'Trail SL toggle'],
    badge: { label: 'Avg setup time', value: '< 8s' },
  },
  {
    id: 'kfxpro',
    name: 'KFX Pro',
    category: 'Smart Money',
    tagline: 'Smart Money — mapped automatically.',
    description: 'Detects FVGs, Order Blocks, and BPRs with NY session levels and auto entry signals directly on your chart.',
    color: '#D4A843', rgb: '212,168,67',
    tvLink: null,
    comingSoon: true,
    thumbnail: kfxProImg,
    features: ['5min FVG detection', 'Asian, London & Newyork session levels', 'Auto entry with Stop loss & Take profit signals', 'BPR zones'],
    badge: { label: 'Signals / session', value: '3–7' },
  },
  {
    id: 'checklist',
    name: 'KFX Checklist',
    category: 'Discipline',
    tagline: 'No checklist, no trade.',
    description: "An on-chart confluence gate that enforces your ICT pre-trade checklist before every entry. Toggle up to 8 custom conditions — Key Level, Manipulation, MSS, FVG/OB/BPR/IFVG and more. Each row shows a live ✅/❌ status, the header tracks your confirmed count (e.g. 3/4), and the footer pins the active symbol and timeframe. If the checklist isn't green, you don't pull the trigger.",
    color: '#5B8FE8', rgb: '91,143,232',
    tvLink: null,
    comingSoon: true,
    thumbnail: kfxChecklistImg,
    features: ['Up to 8 custom conditions', 'Live ✅ / ❌ per row', 'Confirmed / total counter', 'Symbol & TF footer'],
    badge: { label: 'Discipline score', value: '97%' },
  },
  {
    id: 'tradepanel',
    name: 'KFX Trade Panel',
    category: 'MT5 EA',
    tagline: 'One panel. Full trade control.',
    description: "A MetaTrader 5 Expert Advisor that puts every trade management action one click away — instant buy/sell, auto trailing stop, partial close, lot calculator, risk display and scale-in. Runs 24/5 managing your trades automatically.",
    color: '#2ECC8A', rgb: '46,204,138',
    tvLink: null,
    mt5Link: null, // was a direct download — now gated behind "Coming Soon" + WhatsApp CTA
    comingSoon: true,
    thumbnail: kfxTradePanelImg,
    features: ['One-click buy / sell / close', 'Auto trail SL in live market', 'Partial close (25% · 50% · 75%)', 'Risk-based lot calculator', 'Auto breakeven & lock gains', 'Scale-in & flip position'],
    badge: { label: 'Avg execution', value: '< 1s' },
  },
  {
    id: 'fib71ea',
    name: 'KFX Fib71 EA',
    category: 'MT5 EA',
    tagline: 'Draws the zone. Watches the tick. Pulls the trigger.',
    description: "A MetaTrader 5 Expert Advisor that reads any Fibonacci tool you draw on the chart, tracks the 70.5% ICT OTE zone tick-by-tick, and auto-executes with a risk-based lot size — SL at the 100% level, TP at the 0% level. No candle-close lag, no manual entries, one trade per zone.",
    color: '#8B6FE8', rgb: '139,111,232',
    tvLink: null,
    mt5Link: null, // not released yet — comingSoon CTA below
    comingSoon: true,
    thumbnail: kfxFibEaImg,
    features: ['Auto-detects any Fib drawn on chart', '70.5% OTE zone tick execution', 'Risk % based lot calculator', 'Auto SL @ 100% · TP @ 0%', 'Live on-chart status panel', 'One trade per zone (auto re-arm)'],
    badge: { label: 'Entry precision', value: '70.5%' },
  },
];


// ─────────────────────────────────────────────────────────────────
//  SVG MICRO-ICONS
// ─────────────────────────────────────────────────────────────────
const Check = ({ color }) => (
  <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
    <polyline points="1.5,4.5 3.5,6.8 7.5,2.5" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TvIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

const Mt5Icon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2"/>
    <line x1="8" y1="21" x2="16" y2="21"/>
    <line x1="12" y1="17" x2="12" y2="21"/>
  </svg>
);

const WaIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.85.5 3.58 1.46 5.07L2 22l5.2-1.55a9.86 9.86 0 0 0 4.84 1.27h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm5.79 14.05c-.24.68-1.4 1.3-1.94 1.36-.5.06-.95.26-3.2-.67-2.71-1.12-4.42-3.85-4.55-4.03-.13-.18-1.08-1.44-1.08-2.75 0-1.3.68-1.94.93-2.2.24-.27.53-.33.7-.33.18 0 .35 0 .5.01.16.01.38-.06.6.46.23.55.78 1.9.85 2.04.07.14.12.3.02.49-.1.18-.16.3-.3.46-.14.16-.3.36-.43.48-.14.13-.29.27-.13.55.17.29.76 1.25 1.63 2.02 1.12.99 2.05 1.3 2.35 1.44.3.14.47.12.65-.07.18-.2.76-.88.96-1.18.2-.3.4-.25.67-.15.27.1 1.72.81 2.02.96.3.14.5.21.57.33.07.12.07.7-.17 1.38z"/>
  </svg>
);

// ─────────────────────────────────────────────────────────────────
//  WHATSAPP "I'M INTERESTED" CTA — used on every Coming Soon card
// ─────────────────────────────────────────────────────────────────
function WhatsappCta({ toolName }) {
  return (
    <button
      className="ki-cta-whatsapp"
      onClick={() => window.open(buildWhatsappLink(toolName), '_blank', 'noopener noreferrer')}
    >
      <WaIcon /> 🎁 Claim Discount — Text Us on WhatsApp
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────
//  STAT CARD
// ─────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, sup, sub, ctx, color, rgb, badge, loading }) {
  return (
    <div className="ki-stat" style={{ '--sc': color, '--sc-rgb': rgb }}>
      <div className="ki-stat-top">
        <div className="ki-stat-icon">{icon}</div>
        {badge && <span className="ki-stat-badge">{badge}</span>}
      </div>
      <div className="ki-stat-lbl">{label}</div>
      {loading
        ? <div className="ki-skel" />
        : (
          <div className="ki-stat-val">
            {value}
            {sup && <sup>{sup}</sup>}
            {sub && <sub>{sub}</sub>}
          </div>
        )
      }
      <div className="ki-stat-ctx">{loading ? 'Loading…' : ctx}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
//  FEATURED INDICATOR CARD
// ─────────────────────────────────────────────────────────────────
function FeaturedCard({ ind }) {
  const [err, setErr] = useState(false);
  return (
    <div className="ki-feat">
      <div className="ki-feat-img-wrap" style={{ display: 'flex', alignItems: 'stretch' }}>
        {!err
          ? <img src={ind.thumbnail} alt={ind.name} onError={() => setErr(true)}
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center', display: 'block' }} />
          : <div className="ki-img-fallback ki-img-fallback--lg">📊</div>
        }
        <div className="ki-feat-img-fade" />
      </div>

      <div className="ki-feat-body" style={{ '--ic': ind.color, '--ic-rgb': ind.rgb }}>
        <div className="ki-card-meta">
          <div className="ki-cat">
            <span className="ki-cat-dot" style={{ '--ic': ind.color }} />
            {ind.category}
          </div>
          {ind.category === 'MT5 EA'
            ? <span className="ki-mt5-badge">MT5 EA</span>
            : <span className="ki-tv-badge">TradingView</span>
          }
        </div>

        <h2 className="ki-card-title">{ind.name}</h2>
        <p className="ki-card-desc">{ind.description}</p>

        <div className="ki-highlight">
          <span className="ki-hl-val">{ind.badge.value}</span>
          <span className="ki-hl-lbl">{ind.badge.label}</span>
        </div>

        <div className="ki-feats">
          {ind.features.map(f => (
            <div key={f} className="ki-feat-item">
              <div className="ki-feat-tick-gold"><Check color={ind.color} /></div>
              {f}
            </div>
          ))}
        </div>

        {ind.tvLink
          ? <button className="ki-cta-gold" onClick={() => window.open(ind.tvLink,'_blank','noopener noreferrer')}><TvIcon /> Open in TradingView</button>
          : ind.comingSoon
            ? <div className="ki-cta-stack">
                <div className="ki-cta-early"><span className="ki-cta-dot" />🔒 Pre-Launch — Founding Price Available</div>
                <WhatsappCta toolName={ind.name} />
              </div>
            : <div className="ki-cta-early"><span className="ki-cta-dot" />Early Access — Coming Soon</div>
        }
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
//  STANDARD INDICATOR CARD
// ─────────────────────────────────────────────────────────────────
function IndCard({ ind }) {
  const [err, setErr] = useState(false);
  return (
    <div className="ki-card" style={{ '--ic': ind.color, '--ic-rgb': ind.rgb }}>
      <div className="ki-card-img" style={{ aspectRatio: '16/7', overflow: 'hidden' }}>
        {!err
          ? <img src={ind.thumbnail} alt={ind.name} onError={() => setErr(true)}
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center', display: 'block' }} />
          : <div className="ki-img-fallback">📊</div>
        }
        <div className="ki-card-img-fade" />
      </div>

      <div className="ki-card-body">
        <div className="ki-card-meta">
          <div className="ki-cat">
            <span className="ki-cat-dot" />
            {ind.category}
          </div>
          {ind.category === 'MT5 EA'
            ? <span className="ki-mt5-badge">MT5 EA</span>
            : <span className="ki-tv-badge">TradingView</span>
          }
        </div>
        <h3 className="ki-card-title">{ind.name}</h3>
        <p className="ki-card-desc">{ind.tagline}</p>

        <div className="ki-feats ki-feats--grow">
          {ind.features.map(f => (
            <div key={f} className="ki-feat-item">
              <div className="ki-feat-tick"><Check color={ind.color} /></div>
              {f}
            </div>
          ))}
        </div>

        <div style={{ marginTop: 'auto' }}>
        {ind.tvLink
          ? <button className="ki-cta-outline" onClick={() => window.open(ind.tvLink,'_blank','noopener noreferrer')}><TvIcon /> Open in TradingView</button>
          : ind.mt5Link
            ? <button className="ki-cta-mt5" onClick={() => window.open(ind.mt5Link,'_blank','noopener noreferrer')}><Mt5Icon /> Download for MT5</button>
            : ind.comingSoon
              ? <div className="ki-cta-stack">
                  <div className="ki-cta-early"><span className="ki-cta-dot" />🔒 Pre-Launch — Founding Price Available</div>
                  <WhatsappCta toolName={ind.name} />
                </div>
              : <div className="ki-cta-early"><span className="ki-cta-dot" />Launching Soon</div>
        }
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
//  INSIGHT CHIP
// ─────────────────────────────────────────────────────────────────
function InsightChip({ icon, label, value, ctx, cls, loading }) {
  return (
    <div className="ki-ins">
      <div className="ki-ins-icon">{icon}</div>
      <div className="ki-ins-body">
        <div className="ki-ins-lbl">{label}</div>
        {loading
          ? <div className="ki-ins-skel" />
          : <div className={`ki-ins-val ${cls || ''}`}>{value}</div>
        }
        <div className="ki-ins-ctx">{loading ? '' : ctx}</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
//  PAGE
// ─────────────────────────────────────────────────────────────────
export default function IndicatorsPage({ activeAccountId = null, trades: allTrades = [] }) {
  const now     = new Date();
  const dateStr = now.toLocaleDateString('en-GB', { weekday:'short', day:'numeric', month:'short', year:'numeric' });

  // Filter to active account (same logic as DashboardPage)
  const trades = activeAccountId
    ? allTrades.filter(t => t.accountId === activeAccountId || !t.accountId)
    : allTrades;

  // Compute win streak client-side — same function used by DashboardPage stat cards
  const clientStreak = calcConsecWinStreak(trades);

  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // Reset to loading state whenever the account changes
  useEffect(() => { setLoading(true); setStats(null); setError(null); }, [activeAccountId]);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const data = await tradesApi.stats(activeAccountId);
        if (!dead) setStats(data);
      } catch (e) {
        console.error('[IndicatorsPage] stats fetch failed:', e?.message, e?.status, e);
        if (!dead) {
          setError(e?.message || 'Unknown error');
          setStats({
            total: 0, wins: 0, losses: 0, breakevens: 0,
            wr: 0, net: 0, avgrr: '0.00', pf: '0.00',
            bestPair: '—', maxWinStreak: 0,
            bestDay: '—', worstDay: '—',
            expectancy: '0.00', longWR: 0, shortWR: 0,
          });
        }
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => { dead = true; };
  }, [activeAccountId]);

  // ── derived ───────────────────────────────────────────────────
  const wr       = stats?.wr           ?? 0;
  const net      = stats?.net          ?? 0;
  const total    = stats?.total        ?? 0;
  const avgrr    = stats?.avgrr        ?? '0.00';
  const bestPair = stats?.bestPair     ?? '—';
  const streak   = clientStreak;                   // ← computed client-side, same as DashboardPage
  const pf       = stats?.pf           ?? '0.00';
  const bestDay  = stats?.bestDay      ?? '—';

  const netAbs   = Math.abs(net);
  const netFmt   = netAbs >= 10000
    ? `${net >= 0 ? '+' : '-'}$${(netAbs/1000).toFixed(1)}k`
    : `${net >= 0 ? '+' : '-'}$${netAbs.toLocaleString(undefined,{minimumFractionDigits:0, maximumFractionDigits:0})}`;
  const netColor = net >= 0 ? '#2ECC8A' : '#E05555';
  const netRgb   = net >= 0 ? '46,204,138' : '224,85,85';

  const pfNum    = parseFloat(pf);

  // All indicators rendered as equal cards in 2x2 grid

  return (
    <div className="ki">

        {/* ── Header ── */}
        <header className="ki-hdr">
          <div className="ki-hdr-l">
            <h1 className="ki-title">
              KFX <span className="ki-title-gold">Indicator Suite</span>
            </h1>
            <p className="ki-sub">
              {INDICATORS.length} tools active · Invite-only · Your edge, built into the chart
            </p>
          </div>
          <div className="ki-hdr-r">
            <div className="ki-date">{dateStr}</div>
            <button className="ki-tv-btn" onClick={() => window.open('https://tradingview.com','_blank','noopener noreferrer')}>
              <TvIcon /> Open TradingView
            </button>
          </div>
        </header>

        {/* ── Stats row ── */}
        <div className="ki-stats">
          <StatCard
            icon="🎯" label="Win Rate"
            value={loading ? '' : `${wr}`} sup={loading ? '' : '%'}
            ctx={loading ? '' : `${total} closed trades`}
            color="#2ECC8A" rgb="46,204,138"
            badge={!loading && wr >= 60 ? 'Above avg' : null}
            loading={loading}
          />
          <StatCard
            icon="💰" label="Net P&L"
            value={loading ? '' : netFmt}
            ctx={loading ? '' : net >= 0 ? 'Overall profit' : 'Overall loss'}
            color={netColor} rgb={netRgb}
            badge={!loading && total > 0 ? (net >= 0 ? 'Profit' : 'Loss') : null}
            loading={loading}
          />
          <StatCard
            icon="⚖️" label="Avg Risk : Reward"
            value={loading ? '' : avgrr} sub={loading ? '' : 'R'}
            ctx={loading ? '' : 'Across all closed trades'}
            color="#D4A843" rgb="212,168,67"
            loading={loading}
          />
          <StatCard
            icon="⚡" label="Clean Weeks"
            value={loading ? '' : `${streak}`} sub={loading ? '' : streak === 0 ? '' : streak === 1 ? ' week' : ' weeks'}
            ctx={loading ? '' : streak > 0 ? 'Consecutive profitable weeks' : 'No clean weeks yet'}
            color="#D4A843" rgb="212,168,67"
            badge={!loading && streak > 0 ? `${streak} week${streak > 1 ? 's' : ''}` : null}
            loading={loading}
          />
        </div>

        {/* ── Indicator cards ── */}
        <div>
          <div className="ki-sec">INDICATORS</div>
          <div className="ki-grid">
            {INDICATORS.map(ind => <IndCard key={ind.id} ind={ind} />)}
          </div>
        </div>

        {/* ── Performance insights ── */}
        <div>
          <div className="ki-sec">Your Performance</div>
          <div className="ki-insights">
            <InsightChip
              icon="📊" label="Best Instrument"
              value={loading ? '' : bestPair}
              ctx={loading ? '' : bestPair !== '—' ? 'Highest win-rate pair' : 'Log more trades to unlock'}
              cls={bestPair !== '—' ? 'pos' : ''}
              loading={loading}
            />
            <InsightChip
              icon="🏆" label="Profit Factor"
              value={loading ? '' : pf}
              ctx={loading ? '' : pfNum >= 1.5 ? 'Strong edge' : pfNum >= 1 ? 'Break-even or better' : 'Room to improve'}
              cls={pfNum >= 1.5 ? 'pos' : pfNum >= 1 ? 'warn' : ''}
              loading={loading}
            />
            <InsightChip
              icon="📅" label="Best Day"
              value={loading ? '' : bestDay}
              ctx={loading ? '' : 'Highest single-day P&L'}
              cls=""
              loading={loading}
            />
          </div>
        </div>

        {/* ── Footer ── */}
        <footer className="ki-footer">
          <span className="ki-footer-note">
            Indicators are invite-only · <strong>Do not share your access links.</strong>
          </span>
          <span className="ki-footer-brand">KFX University · {now.getFullYear()}</span>
        </footer>

    </div>
  );
}